import { DOCUMENT, ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { trackDownloadClick } from '@ledgerly/common/browser';

/** Verified against the live listings; change here, not in a template. */
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/ledgerly-budget-expense/id6755126067';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.valerymelou.ledgerly';

/**
 * Locales we hold official badge artwork for.
 *
 * These files cannot be generated. The App Store badge is 25 outlined `<path>`
 * elements, not text, and the Play badge is a PNG. Both are trademarked
 * lockups whose brand guidelines require the supplied artwork, so redrawing
 * "Laden im App Store" as vector outlines would mean guessing letterforms and
 * shipping a non-compliant badge.
 *
 * To add a language, download both files, drop them in
 * `apps/landing/public/assets/images/` under the existing naming convention,
 * and add the code here. `npx nx run landing:check-badges` lists exactly which
 * files are missing and where to get them.
 *
 *   App Store  https://developer.apple.com/app-store/marketing/guidelines/
 *   Play Store https://play.google.com/intl/en_us/badges/
 */
export const BADGE_LOCALES = ['en', 'fr'] as const;

/** Anything without artwork of its own borrows English rather than 404ing. */
const BADGE_FALLBACK = 'en';

/**
 * The two store badges.
 *
 * `onBand` selects the dark App Store artwork regardless of theme, because an
 * element sitting on the band keeps its dark construction in both themes
 * (spec §F.2.3). Off the band, the badge follows the page.
 */
@Component({
  selector: 'layout-store-badges',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-wrap items-center gap-x-6 gap-y-4">
      <a
        [href]="appStoreUrl"
        target="_blank"
        rel="noopener"
        aria-label="Download Ledgerly on the App Store"
        i18n-aria-label="@@storeAppleAria"
        (click)="track()"
      >
        <img
          [src]="appleBadge()"
          class="h-[40px] w-auto"
          width="120"
          height="40"
          alt="Download on the App Store"
          i18n-alt="@@storeAppleAlt"
        />
      </a>

      <a
        [href]="playStoreUrl"
        target="_blank"
        rel="noopener"
        aria-label="Get Ledgerly on Google Play"
        i18n-aria-label="@@storeGoogleAria"
        (click)="track()"
      >
        <img
          [src]="googleBadge()"
          class="h-[40px] w-auto"
          width="135"
          height="40"
          alt="Get it on Google Play"
          i18n-alt="@@storeGoogleAlt"
        />
      </a>
    </div>
  `,
})
export class StoreBadgesComponent {
  /** Where the click happened, for the analytics label. */
  readonly location = input.required<string>();

  /** True when the badges sit on the indigo band. */
  readonly onBand = input(false);

  readonly appStoreUrl = APP_STORE_URL;
  readonly playStoreUrl = PLAY_STORE_URL;

  private readonly document = inject(DOCUMENT);

  /** `en` and `fr` have artwork; everything else borrows English. */
  private readonly badgeLocale = signal(
    (BADGE_LOCALES as readonly string[]).includes(
      this.document.documentElement.lang
    )
      ? this.document.documentElement.lang
      : BADGE_FALLBACK
  );

  protected readonly appleBadge = computed(
    () =>
      `/assets/images/get-it-on-app-store-${this.badgeLocale()}-${
        this.onBand() ? 'dark' : 'light'
      }.svg`
  );

  protected readonly googleBadge = computed(
    () => `/assets/images/get-it-on-google-play-${this.badgeLocale()}.png`
  );

  protected track(): void {
    trackDownloadClick(this.location());
  }
}
